<template>
  <div class="banner">
    <div
      v-if="!isMobile"
      class="banner-pc"
    >
      <div class="download-app">
        <div class="download-banner">
          <picture class="hero">
            <source
              srcset="@/assets/images/pc/banner.webp"
              type="image/webp"
            />
            <img
              src="@/assets/images/pc/banner.jpg"
              width="3840"
              height="1560"
              fetchpriority="high"
              alt=""
            />
          </picture>
        </div>
        <button
          type="button"
          class="download-button"
          :disabled="loading || downloading"
          @click="goDownload"
        >
          立即下載
        </button>
        <img
          src="@/assets/images/pc/banner1.jpg"
          class="banner1"
          width="2400"
          height="6376"
          loading="lazy"
          decoding="async"
          alt=""
        />
        <img
          src="@/assets/images/pc/banner2.png"
          class="banner2"
          width="2400"
          height="1668"
          loading="lazy"
          decoding="async"
          alt=""
        />
      </div>
    </div>
    <!-- h5 -->
    <BannerH5
      v-else
      class="banner-h5-wrap"
      :downloading="loading || downloading"
      @download="goDownload"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { downloadApp, parseUA } from '@local-utils/common';
import { useAttribution } from '@local-composable/common';
import { useRequest } from '@local-composable/request';
import { createFingerprint, ident_uid } from '@local-utils/burial-point';
import { showLoadingToast } from 'vant';
import BannerH5 from './banner-h5.vue';

const props = defineProps({
  isMobile: Boolean,
  loading: Boolean,
  channel: { type: String, default: '' },
  linkId: { type: Number, default: null },
  autoOpenAppStore: Boolean,
});
const downloading = ref(false);
const clickParam = {};
const attribution =
  props.channel && props.linkId
    ? useAttribution({ channel: props.channel, isToStore: false, clickParam })
    : null;
const visitorId = attribution
  ? new Promise((resolve) =>
      createFingerprint(() => {
        try {
          localStorage.setItem('USER_IDENT', ident_uid);
        } catch {
          // 禁用本地存储时仍使用本次生成的指纹上报。
        }
        resolve(ident_uid);
      }),
    )
  : null;

const reportEvent = async (eventType) => {
  if (!attribution) return;
  try {
    const visitor = await visitorId;
    if (!visitor) return;
    await useRequest(
      {
        url: '/v2/app_deep_link/event',
        data: {
          link_id: props.linkId,
          event_type: eventType,
          visitor_id: visitor,
          channel: props.channel,
        },
      },
      { toast: false, keepalive: true },
      { timeout: 3000 },
    ).post();
  } catch (error) {
    console.warn('Deep link event unavailable', error);
  }
};

let pageEvent;
onMounted(() => {
  pageEvent = reportEvent(3);
  return props.autoOpenAppStore && attribution ? goDownload() : pageEvent;
});

const goDownload = async () => {
  if (props.loading || downloading.value) return;
  if (!attribution) {
    downloadApp();
    return;
  }
  downloading.value = true;
  const loading = showLoadingToast({
    duration: 0,
    forbidClick: true,
    message: 'loading...',
  });
  const eventRequest = reportEvent(4);
  let urls;
  try {
    const shareUrl = new URL(window.location.href);
    shareUrl.searchParams.set('channel', props.channel);
    shareUrl.searchParams.set('type', 'new');
    if (clickParam.share_url !== shareUrl.href) {
      // 同渠道的旧归因可能属于其他链接，需重新请求以绑定当前 share_url。
      localStorage.removeItem(`attribution_click_data_${props.channel}`);
      attribution.attribution_id.value = '';
      clickParam.share_url = shareUrl.href;
    }
    urls = await attribution.toDownload();
  } catch (e) {
    console.error(e);
  } finally {
    await Promise.all([pageEvent, eventRequest]);
    loading.close();
    downloading.value = false;
    if (urls) {
      const ua = parseUA();
      window.location.href = ua.ios || ua.macos ? urls.ios : urls.android;
    } else {
      downloadApp();
    }
  }
};

defineExpose({ goDownload });
</script>

<style lang="scss" scoped>
@import '@/assets/scss/common.scss';

.banner {
  max-width: $pc-max-width;
  background-color: #ffffff;
  overflow: hidden;
  padding-bottom: 2rem;

  .download-app {
    .download-banner {
      position: relative;
      box-sizing: border-box;
      width: 100%;
      aspect-ratio: 3840 / 1560;

      .hero {
        position: absolute;
        inset: 0;
        pointer-events: none;

        img {
          display: block;
          width: 100%;
          height: auto;
        }
      }
    }

    .download-button {
      position: relative;
      z-index: 6;
      width: 4rem;
      height: 0.8rem;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 0.56rem;
      margin: -3.2rem auto 0.5rem;
      padding: 0 0.22rem;
      border-radius: 0.12rem;
      background: linear-gradient(to bottom, #ffe7a8, #fff5d7);
      border: 1px solid #ffe7aa;
      cursor: pointer;
      font-size: 0.28rem;
      line-height: 1.2;
      color: #1a1208;
    }

    .banner1 {
      position: relative;
      z-index: 5;
      display: block;
      width: 62.5%;
      height: auto;
      aspect-ratio: 2400 / 6376;
      margin: 0 auto;
    }

    .banner2 {
      display: block;
      width: 62.5%;
      height: auto;
      aspect-ratio: 2400 / 1668;
      margin: 1.5rem auto 0;
    }
  }

  .more {
    display: none;
  }

  .banner-h5-wrap {
    display: none;
  }
}

@media (max-width: 768px) {
  .banner {
    max-width: none;
    padding-bottom: 0;

    .banner-pc {
      display: none;
    }

    .banner-h5-wrap {
      display: block;
    }
  }
}
</style>
